<template>
  <Layout>
    <div class="form-builder">
      <!-- Header -->
      <div class="builder-header">
        <div class="header-left">
          <button class="btn-back" @click="goBack">
            <i class="bx bx-arrow-back"></i>
          </button>
          <div class="form-title-section">
            <input
              v-model="templateName"
              type="text"
              class="form-title-input"
              placeholder="Nome do template"
              @input="onFieldChange('name', $event.target.value)"
            />
            <span class="form-status" :class="isActive ? 'active' : 'inactive'">
              {{ isActive ? 'Ativo' : 'Inativo' }}
            </span>
          </div>
        </div>
        <div class="header-right">
          <button class="btn-settings" @click="showSettings = true">
            <i class="bx bx-cog"></i>
            Configurações
          </button>
          <button class="btn-preview" @click="showPreviewModal = true">
            <i class="bx bx-show"></i>
            Preview
          </button>
          <button
            v-if="template && template.id"
            class="btn-history"
            @click="openHistory"
            title="Histórico de envios"
          >
            <i class="bx bx-history"></i>
            Histórico
          </button>
          <button class="btn-save" @click="saveTemplate" :disabled="saving">
            <i class="bx" :class="saving ? 'bx-loader-alt bx-spin' : 'bx-save'"></i>
            {{ saving ? 'Salvando...' : 'Salvar' }}
          </button>
        </div>
      </div>

      <!-- Loading Overlay -->
      <transition name="fade-overlay">
        <div v-if="loading" class="builder-loading-overlay">
          <div class="builder-loading-content">
            <div class="builder-loading-spinner"></div>
            <p>Carregando template...</p>
          </div>
        </div>
      </transition>

      <!-- Main Content -->
      <div class="builder-content">
        <!-- Left Panel: Block Palette -->
        <div class="panel panel-left">
          <BlockPalette
            @add-block="handleAddBlock"
            @load-preset="handleLoadPreset"
          />
        </div>

        <!-- Center Panel: Document Canvas -->
        <div class="panel panel-center">
          <div class="canvas-header">
            <h5>
              <i class="bx bx-file"></i>
              Documento
            </h5>
            <span class="field-count">{{ blocks.length }} blocos</span>
          </div>

          <div class="canvas-scroll">
            <DocumentCanvas
              :blocks="blocks"
              :selected-block-id="selectedBlockId"
              :header-image="headerImage"
              :header-mode="headerMode"
              @select="handleSelectBlock"
              @reorder="handleReorderBlocks"
              @remove="handleRemoveBlock"
              @duplicate="handleDuplicateBlock"
              @drop-new="handleAddBlock"
              @header-click="triggerHeaderUpload"
            />
            <input
              ref="headerFileInput"
              type="file"
              accept="image/*"
              style="display:none"
              @change="uploadHeaderImage"
            />
          </div>
        </div>

        <!-- Right Panel: Block Editor -->
        <div class="panel panel-right" :class="{ 'has-selection': selectedBlock }">
          <div v-if="!selectedBlock" class="no-selection">
            <div class="no-selection-icon">
              <i class="bx bx-pointer"></i>
            </div>
            <h5>Nenhum bloco selecionado</h5>
            <p>Clique em um bloco para editar suas propriedades</p>
          </div>

          <BlockEditor
            v-else
            :block="selectedBlock"
            @update="handleBlockUpdate"
            @close="handleSelectBlock(null)"
          />
        </div>
      </div>

      <!-- Settings Modal -->
      <b-modal
        v-model="showSettings"
        title="Configurações do Template"
        size="lg"
        ok-title="Salvar"
        cancel-title="Cancelar"
        @ok="saveSettingsModal"
        centered
      >
        <b-tabs content-class="mt-3">
          <b-tab title="Geral" active>
        <div class="settings-form">
          <div class="form-group">
            <label>Nome do Template *</label>
            <input
              v-model="templateName"
              type="text"
              class="form-control"
              placeholder="Nome do template"
              @input="onFieldChange('name', $event.target.value)"
            />
          </div>

          <div class="form-group">
            <label>Imagem do Topo</label>
            <div class="header-mode-selector">
              <button
                class="header-mode-btn"
                :class="{ active: headerMode === 'logo' }"
                @click="headerMode = 'logo'; onFieldChange('header_mode', 'logo')"
              >
                <i class="bx bx-image"></i>
                Logo
              </button>
              <button
                class="header-mode-btn"
                :class="{ active: headerMode === 'banner' }"
                @click="headerMode = 'banner'; onFieldChange('header_mode', 'banner')"
              >
                <i class="bx bx-expand-horizontal"></i>
                Banner
              </button>
            </div>
            <div v-if="headerMode === 'banner'" class="banner-recommendation">
              <i class="bx bx-info-circle"></i>
              <div>
                <span>Tamanho recomendado: <strong>1920 x 300 px</strong></span>
              </div>
            </div>
            <div
              v-if="!headerImage"
              class="upload-dropzone"
              @click="$refs.settingsFileInput.click()"
              @dragover.prevent
              @drop.prevent="onDropFile"
            >
              <i class="bx bx-cloud-upload"></i>
              <span>Clique ou arraste uma imagem</span>
              <small>PNG, JPG ou SVG</small>
            </div>
            <div v-else class="upload-preview">
              <img :src="headerImage" alt="Header" />
              <div class="upload-preview-actions">
                <button class="btn-upload-change" @click="$refs.settingsFileInput.click()">
                  <i class="bx bx-refresh"></i> Trocar
                </button>
                <button class="btn-upload-remove" @click="removeHeaderImage">
                  <i class="bx bx-trash"></i> Remover
                </button>
              </div>
            </div>
            <input
              ref="settingsFileInput"
              type="file"
              accept="image/*"
              style="display: none"
              @change="uploadHeaderImage"
            />
          </div>

          <div class="form-group">
            <label>Público-alvo (Grupos)</label>
            <multiselect
              v-model="targetGroups"
              :options="groups"
              :multiple="true"
              track-by="id"
              label="name"
              placeholder="Todos os grupos"
              :close-on-select="false"
              @input="onTargetGroupsChange"
            />
          </div>

          <div class="form-group">
            <label>Público-alvo (Tags)</label>
            <multiselect
              v-model="targetTags"
              :options="tags"
              :multiple="true"
              track-by="id"
              label="name"
              placeholder="Todas as tags"
              :close-on-select="false"
              @input="onTargetTagsChange"
            />
          </div>

          <div class="form-group">
            <b-form-checkbox
              v-model="isActive"
              switch
              @change="onFieldChange('is_active', $event)"
            >
              Template ativo
            </b-form-checkbox>
          </div>
        </div>
          </b-tab>

          <b-tab title="E-mail de envio">
            <div class="settings-form">
              <div class="email-editor-intro">
                <i class="bx bx-info-circle"></i>
                <div>
                  <strong>Assunto e corpo do e-mail</strong> que acompanhará o PDF anexo.
                  Use as merge tags abaixo para personalizar por participante.
                </div>
              </div>

              <div class="form-group">
                <label>Assunto *</label>
                <input
                  type="text"
                  class="form-control"
                  :value="emailSubject"
                  placeholder="Ex: Carta Pax - {$evento}"
                  @input="onEmailSubjectChange($event.target.value)"
                />
                <div class="email-tag-buttons mt-2">
                  <span class="email-tag-label">Inserir tag:</span>
                  <button
                    v-for="t in emailMergeTags"
                    :key="'subj-' + t.var"
                    type="button"
                    class="email-tag-btn"
                    @click="insertEmailSubjectTag(t.var)"
                    :title="t.var"
                  >
                    {{ t.label }}
                  </button>
                </div>
              </div>

              <div class="form-group">
                <label>Corpo do e-mail</label>
                <div class="email-tag-buttons mb-2">
                  <span class="email-tag-label">Inserir tag:</span>
                  <button
                    v-for="t in emailMergeTags"
                    :key="'body-' + t.var"
                    type="button"
                    class="email-tag-btn"
                    @click="insertEmailBodyTag(t.var)"
                    :title="t.var"
                  >
                    {{ t.label }}
                  </button>
                </div>
                <div class="email-editor-ck">
                  <ckeditor
                    :editor="emailEditorClass"
                    :value="emailBody"
                    :config="emailEditorConfig"
                    @input="onEmailBodyChange"
                  />
                </div>
              </div>

              <div class="form-group">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary"
                  @click="resetEmailDefaults"
                >
                  <i class="bx bx-reset"></i> Restaurar texto padrão
                </button>
              </div>

              <div class="form-group">
                <label>Preview</label>
                <div class="email-preview">
                  <div class="email-preview-subject">
                    <span class="email-preview-label">Assunto:</span>
                    {{ emailSubject || '(sem assunto)' }}
                  </div>
                  <div class="email-preview-body" v-html="emailBody || '<p class=\'text-muted\'>(corpo vazio)</p>'"></div>
                  <div class="email-preview-note">
                    <i class="bx bx-paperclip"></i> PDF da Carta Pax será anexado automaticamente
                  </div>
                </div>
              </div>
            </div>
          </b-tab>
        </b-tabs>
      </b-modal>

      <!-- Preview Modal -->
      <b-modal
        v-model="showPreviewModal"
        title="Preview do Documento"
        size="md"
        hide-footer
        centered
      >
        <div class="form-group">
          <label>Buscar participante</label>
          <input
            type="text"
            class="form-control"
            v-model="previewSearch"
            placeholder="Nome ou email..."
            @input="debouncePreviewSearch"
          />
        </div>

        <div v-if="previewResults.length > 0" class="preview-results">
          <div
            v-for="p in previewResults"
            :key="p.id"
            class="preview-result-item"
            :class="{ selected: previewParticipantId === p.id }"
            @click="previewParticipantId = p.id"
          >
            <strong>{{ p.name }}</strong>
            <small>{{ p.email }}</small>
          </div>
        </div>

        <button
          class="btn btn-primary btn-block mt-3"
          @click="generatePreview"
          :disabled="!previewParticipantId || previewLoading"
        >
          <b-spinner small v-if="previewLoading" class="mr-1"></b-spinner>
          <i class="bx bx-file" v-else></i>
          {{ previewLoading ? 'Salvando e gerando...' : 'Gerar PDF Preview' }}
        </button>

        <div v-if="template && template.id" class="mt-3">
          <button
            class="btn btn-outline-primary btn-block"
            @click="openGenerate"
          >
            <i class="bx bx-file-blank"></i> Gerar Cartas em Lote
          </button>
        </div>
      </b-modal>

      <!-- Generate Modal -->
      <GenerateModal
        v-if="template && template.id"
        :templateId="template.id"
        ref="generateModal"
      />

      <!-- Sends History Modal -->
      <SendsHistoryModal
        v-if="template && template.id"
        :templateId="template.id"
        ref="sendsHistoryModal"
      />
    </div>
  </Layout>
</template>

<script>
import Layout from '@/components/layout/vertical'
import Multiselect from 'vue-multiselect'
import CKEditor from '@ckeditor/ckeditor5-vue'
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'
import BlockPalette from './components/BlockPalette.vue'
import DocumentCanvas from './components/DocumentCanvas.vue'
import BlockEditor from './components/BlockEditor.vue'
import GenerateModal from './components/GenerateModal.vue'
import SendsHistoryModal from './components/SendsHistoryModal.vue'
import services from './services'
import { mapGetters, mapActions } from 'vuex'

var DEFAULT_EMAIL_SUBJECT = 'Carta Pax - {$evento}'
var DEFAULT_EMAIL_BODY = '<p>Olá, <strong>{$nome}</strong>!</p><p>Segue em anexo sua <strong>Carta Pax</strong> com as informações sobre o evento <strong>{$evento}</strong>.</p><p>Recomendamos que você leia o documento com atenção e o mantenha à mão durante toda a viagem.</p><p>Atenciosamente,</p>'

var previewSearchTimeout = null

export default {
  name: 'CartaPaxBuilder',
  components: {
    Layout,
    Multiselect,
    BlockPalette,
    DocumentCanvas,
    BlockEditor,
    GenerateModal,
    SendsHistoryModal,
    ckeditor: CKEditor.component
  },
  data: function () {
    return {
      templateName: '',
      templateDescription: '',
      headerImage: null,
      headerMode: 'logo',
      targetGroups: [],
      targetTags: [],
      isActive: true,
      showSettings: false,
      emailSubject: '',
      emailBody: '',
      emailEditorClass: ClassicEditor,
      emailEditorConfig: {
        language: 'pt-br',
        toolbar: {
          items: [
            'bold', 'italic', 'underline', '|',
            'bulletedList', 'numberedList', '|',
            'link', '|',
            'undo', 'redo'
          ]
        }
      },
      emailMergeTags: [
        { var: '{$nome}', label: 'Nome' },
        { var: '{$email}', label: 'Email' },
        { var: '{$grupo}', label: 'Grupo' },
        { var: '{$evento}', label: 'Evento' },
        { var: '{$data_atual}', label: 'Data' },
        { var: '{$hotel}', label: 'Hotel' },
        { var: '{$localizador}', label: 'Localizador' }
      ],
      showPreviewModal: false,
      previewSearch: '',
      previewResults: [],
      previewParticipantId: null,
      previewLoading: false
    }
  },
  computed: {
    ...mapGetters('cartaPax', ['TEMPLATE', 'BLOCKS', 'SELECTED_BLOCK']),
    template: function () {
      return this.TEMPLATE
    },
    blocks: function () {
      return this.BLOCKS
    },
    selectedBlock: function () {
      return this.SELECTED_BLOCK
    },
    selectedBlockId: function () {
      return this.$store.state.cartaPax.selectedBlockId
    },
    loading: function () {
      return this.$store.state.cartaPax.loading
    },
    saving: function () {
      return this.$store.state.cartaPax.saving
    },
    groups: function () {
      return this.$store.state.cartaPax.groups || []
    },
    tags: function () {
      return this.$store.state.cartaPax.tags || []
    },
    isEdit: function () {
      return !!this.$route.params.id
    }
  },
  mounted: function () {
    document.body.classList.add('form-builder-page')

    this.LoadReferenceData()

    var self = this
    if (this.isEdit) {
      this.GetTemplate(this.$route.params.id).then(function () {
        self.syncLocalFields()
      })
    } else {
      this.InitNewTemplate()
      this.syncLocalFields()
    }
  },
  beforeDestroy: function () {
    document.body.classList.remove('form-builder-page')
  },
  watch: {
    TEMPLATE: {
      handler: function () {
        this.syncLocalFields()
      },
      deep: true
    }
  },
  methods: {
    ...mapActions('cartaPax', [
      'GetTemplate',
      'SaveTemplate',
      'InitNewTemplate',
      'UpdateTemplateField',
      'LoadReferenceData',
      'AddBlock',
      'UpdateBlock',
      'RemoveBlock',
      'ReorderBlocks',
      'SelectBlock',
      'LoadPresetTemplate'
    ]),

    syncLocalFields: function () {
      if (this.template) {
        this.templateName = this.template.name || ''
        this.templateDescription = this.template.description || ''
        this.headerImage = this.template.header_image || null
        this.headerMode = this.template.header_mode || 'logo'
        this.isActive = this.template.is_active !== undefined ? this.template.is_active : true
        this.emailSubject = this.template.email_subject || DEFAULT_EMAIL_SUBJECT
        this.emailBody = this.template.email_body || DEFAULT_EMAIL_BODY

        if (this.template.target_groups && this.groups.length) {
          var tg = this.template.target_groups
          this.targetGroups = this.groups.filter(function (g) {
            return tg.includes(g.id)
          })
        } else {
          this.targetGroups = []
        }
        if (this.template.target_tags && this.tags.length) {
          var tt = this.template.target_tags
          this.targetTags = this.tags.filter(function (t) {
            return tt.includes(t.id)
          })
        } else {
          this.targetTags = []
        }
      }
    },

    goBack: function () {
      this.$router.push({ name: 'carta-pax' })
    },

    onFieldChange: function (field, value) {
      this.UpdateTemplateField({ field: field, value: value })
    },

    onTargetGroupsChange: function (val) {
      this.UpdateTemplateField({
        field: 'target_groups',
        value: val.length > 0 ? val.map(function (g) { return g.id }) : null
      })
    },

    onTargetTagsChange: function (val) {
      this.UpdateTemplateField({
        field: 'target_tags',
        value: val.length > 0 ? val.map(function (t) { return t.id }) : null
      })
    },

    saveSettingsModal: function () {
      // Settings are already synced via UpdateTemplateField
    },

    onEmailSubjectChange: function (value) {
      this.emailSubject = value
      this.UpdateTemplateField({ field: 'email_subject', value: value })
    },

    onEmailBodyChange: function (value) {
      this.emailBody = value
      this.UpdateTemplateField({ field: 'email_body', value: value })
    },

    insertEmailSubjectTag: function (tag) {
      this.emailSubject = (this.emailSubject || '') + tag
      this.UpdateTemplateField({ field: 'email_subject', value: this.emailSubject })
    },

    insertEmailBodyTag: function (tag) {
      this.emailBody = (this.emailBody || '') + ' <strong>' + tag + '</strong>&nbsp;'
      this.UpdateTemplateField({ field: 'email_body', value: this.emailBody })
    },

    resetEmailDefaults: function () {
      this.emailSubject = DEFAULT_EMAIL_SUBJECT
      this.emailBody = DEFAULT_EMAIL_BODY
      this.UpdateTemplateField({ field: 'email_subject', value: DEFAULT_EMAIL_SUBJECT })
      this.UpdateTemplateField({ field: 'email_body', value: DEFAULT_EMAIL_BODY })
    },

    // --- Block operations ---
    handleAddBlock: function (type) {
      var defaults = {
        text: { content: '<p>Insira o conteúdo aqui...</p>', settings: {} },
        heading: { content: 'TÍTULO DA SEÇÃO', settings: { style: 'banner', alignment: 'center' } },
        'flight-table': { content: '', settings: { source: 'auto', showTitle: true, title: 'VOOS CONFIRMADOS', showLocalizador: true, showEticket: false } },
        accommodation: { content: '', settings: { source: 'auto', showTitle: true, title: 'HOSPEDAGEM', showCompanions: true, showCategory: false, showNotes: false } },
        divider: { content: '', settings: { style: 'solid' } }
      }
      var blockDefault = defaults[type] || { content: '', settings: {} }
      this.AddBlock({
        type: type,
        content: blockDefault.content,
        settings: blockDefault.settings
      })
    },

    handleSelectBlock: function (id) {
      this.SelectBlock(id)
    },

    handleBlockUpdate: function (payload) {
      this.UpdateBlock(payload)
    },

    handleRemoveBlock: function (id) {
      var self = this
      this.$bvModal.msgBoxConfirm(
        'Tem certeza que deseja remover este bloco?',
        {
          title: 'Remover Bloco',
          okTitle: 'Sim, remover',
          okVariant: 'danger',
          cancelTitle: 'Cancelar',
          centered: true
        }
      ).then(function (confirmed) {
        if (confirmed) {
          self.RemoveBlock(id)
        }
      })
    },

    handleDuplicateBlock: function (block) {
      var idx = this.blocks.findIndex(function (b) { return b._id === block._id })
      this.AddBlock({
        type: block.type,
        index: idx + 1,
        content: block.content,
        settings: Object.assign({}, block.settings)
      })
    },

    handleReorderBlocks: function (blocks) {
      this.ReorderBlocks(blocks)
    },

    handleLoadPreset: function (presetName) {
      if (this.blocks.length > 0) {
        var self = this
        this.$bvModal.msgBoxConfirm(
          'Carregar um modelo irá substituir os blocos atuais. Deseja continuar?',
          {
            title: 'Carregar Modelo',
            okTitle: 'Sim, carregar',
            okVariant: 'primary',
            cancelTitle: 'Cancelar',
            centered: true
          }
        ).then(function (confirmed) {
          if (confirmed) {
            self.LoadPresetTemplate(presetName)
          }
        })
      } else {
        this.LoadPresetTemplate(presetName)
      }
    },

    // --- Save ---
    saveTemplate: async function () {
      if (!this.templateName || !this.templateName.trim()) {
        this.$bvToast.toast('Insira o nome do template.', {
          title: 'Atenção',
          variant: 'warning',
          solid: true
        })
        return
      }

      try {
        var res = await this.SaveTemplate()
        this.$bvToast.toast('Template salvo com sucesso!', {
          title: 'Sucesso',
          variant: 'success',
          solid: true
        })

        if (!this.isEdit && res.data.data && res.data.data.id) {
          this.$router.replace({
            name: 'carta-pax-edit',
            params: { id: res.data.data.id }
          })
        }
      } catch (e) {
        this.$bvToast.toast(e.message || 'Erro ao salvar template.', {
          title: 'Erro',
          variant: 'danger',
          solid: true
        })
      }
    },

    // --- Header image ---
    triggerHeaderUpload: function () {
      this.$refs.headerFileInput.click()
    },

    onDropFile: function (event) {
      var file = event.dataTransfer.files[0]
      if (file && file.type.startsWith('image/')) {
        this.uploadHeaderImage({ target: { files: [file] } })
      }
    },

    removeHeaderImage: function () {
      this.headerImage = null
      this.UpdateTemplateField({ field: 'header_image', value: null })
    },

    uploadHeaderImage: async function (event) {
      var file = event.target.files[0]
      if (!file) return

      var formData = new FormData()
      formData.append('image', file)

      try {
        var res = await services.uploadImage(formData)
        this.headerImage = res.data.url
        this.UpdateTemplateField({ field: 'header_image', value: res.data.url })
        this.$bvToast.toast('Imagem enviada com sucesso!', {
          title: 'Sucesso',
          variant: 'success',
          solid: true
        })
      } catch (e) {
        this.$bvToast.toast('Erro ao enviar imagem.', {
          title: 'Erro',
          variant: 'danger',
          solid: true
        })
      }
    },

    // --- Preview ---
    debouncePreviewSearch: function () {
      var self = this
      if (previewSearchTimeout) clearTimeout(previewSearchTimeout)
      previewSearchTimeout = setTimeout(function () {
        self.searchPreviewParticipants()
      }, 400)
    },

    searchPreviewParticipants: async function () {
      if (!this.previewSearch || this.previewSearch.length < 2) {
        this.previewResults = []
        return
      }
      try {
        var res = await services.searchParticipants({
          search: this.previewSearch,
          limit: 10
        })
        this.previewResults = res.data.data || res.data
      } catch (e) {
        console.error(e)
      }
    },

    generatePreview: async function () {
      if (!this.previewParticipantId) {
        this.$bvToast.toast('Selecione um participante para preview.', {
          title: 'Atenção',
          variant: 'warning',
          solid: true
        })
        return
      }

      if (!this.templateName || !this.templateName.trim()) {
        this.$bvToast.toast('Insira o nome do template antes de gerar preview.', {
          title: 'Atenção',
          variant: 'warning',
          solid: true
        })
        return
      }

      this.previewLoading = true
      try {
        // Auto-save before preview so backend has latest content
        var saveRes = await this.SaveTemplate()

        // If it was a new template, update the route
        if (!this.isEdit && saveRes.data.data && saveRes.data.data.id) {
          this.$router.replace({
            name: 'carta-pax-edit',
            params: { id: saveRes.data.data.id }
          })
        }

        var templateId = (saveRes.data.data && saveRes.data.data.id) || this.template.id
        var res = await services.preview(templateId, this.previewParticipantId)

        // Check if response is actually a PDF (blob) or an error JSON
        if (res.data instanceof Blob && res.data.type === 'application/json') {
          // It's an error response disguised as blob
          var text = await res.data.text()
          var errorData = JSON.parse(text)
          throw new Error(errorData.message || 'Erro ao gerar preview.')
        }

        var url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
        window.open(url, '_blank')
      } catch (e) {
        var msg = 'Erro ao gerar preview.'
        if (e.response && e.response.data) {
          // Axios error with blob response — read as text
          if (e.response.data instanceof Blob) {
            try {
              var errText = await e.response.data.text()
              var errJson = JSON.parse(errText)
              msg = errJson.message || msg
            } catch (parseErr) {
              // ignore parse error
            }
          } else if (e.response.data.message) {
            msg = e.response.data.message
          }
        } else if (e.message) {
          msg = e.message
        }
        this.$bvToast.toast(msg, {
          title: 'Erro',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.previewLoading = false
      }
    },

    openGenerate: function () {
      this.showPreviewModal = false
      this.$bvModal.show('generate-modal')
    },

    openHistory: function () {
      this.$bvModal.show('sends-history-modal')
    }
  }
}
</script>

<style scoped>
.form-builder {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f5f6fa;
  overflow: hidden;
  position: relative;
}

/* Header - identical to card-builder */
.builder-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: #fff;
  border-bottom: 1px solid #e9ecef;
  flex-shrink: 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.btn-back {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e9ecef;
  background: #fff;
  border-radius: 8px;
  cursor: pointer;
  color: #495057;
  transition: all 0.15s;
}

.btn-back:hover {
  border-color: #556ee6;
  color: #556ee6;
}

.form-title-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.form-title-input {
  border: none;
  background: transparent;
  font-size: 18px;
  font-weight: 600;
  color: #344054;
  padding: 4px 0;
  width: 300px;
}

.form-title-input:focus {
  outline: none;
  border-bottom: 2px solid #556ee6;
}

.form-status {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
}

.form-status.active {
  background: #d1fae5;
  color: #059669;
}

.form-status.inactive {
  background: #fee2e2;
  color: #dc2626;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-settings {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid #e9ecef;
  background: #fff;
  border-radius: 6px;
  font-size: 13px;
  color: #495057;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-settings:hover {
  border-color: #556ee6;
  color: #556ee6;
}

.btn-preview {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid #17a2b8;
  background: #fff;
  border-radius: 6px;
  font-size: 13px;
  color: #17a2b8;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-preview:hover {
  background: #17a2b8;
  color: #fff;
}

.btn-history {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid #7c3aed;
  background: #fff;
  border-radius: 6px;
  font-size: 13px;
  color: #7c3aed;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-history:hover {
  background: #7c3aed;
  color: #fff;
}

.btn-save {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 20px;
  border: none;
  background: #556ee6;
  color: #fff;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-save:hover:not(:disabled) {
  background: #485ec4;
}

.btn-save:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Main Content */
.builder-content {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.panel {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.panel-left {
  width: 320px;
  border-right: 1px solid #e9ecef;
  background: #fff;
  flex-shrink: 0;
}

.panel-center {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: #f5f6fa;
}

.canvas-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: #fff;
  border-bottom: 1px solid #e9ecef;
}

.canvas-header h5 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #495057;
  display: flex;
  align-items: center;
  gap: 8px;
}

.canvas-header h5 i {
  color: #556ee6;
}

.canvas-header .field-count {
  font-size: 12px;
  color: #6c757d;
}

.canvas-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  min-height: 0;
  scrollbar-width: thin;
  scrollbar-color: #d0d5dd transparent;
}

.canvas-scroll::-webkit-scrollbar {
  width: 6px;
}

.canvas-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.canvas-scroll::-webkit-scrollbar-thumb {
  background-color: #d0d5dd;
  border-radius: 3px;
}

.canvas-scroll::-webkit-scrollbar-thumb:hover {
  background-color: #b0b5bd;
}

.panel-right {
  width: 0;
  background: #fff;
  border-left: 1px solid #e9ecef;
  transition: width 0.2s ease;
  overflow: hidden;
  flex-shrink: 0;
}

.panel-right.has-selection {
  width: 360px;
}

.no-selection {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 32px;
  text-align: center;
}

.no-selection-icon {
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f4ff;
  border-radius: 50%;
  margin-bottom: 16px;
}

.no-selection-icon i {
  font-size: 28px;
  color: #556ee6;
}

.no-selection h5 {
  font-size: 15px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 8px;
}

.no-selection p {
  font-size: 13px;
  color: #6c757d;
  margin: 0;
}

/* Settings Form */
.settings-form .form-group {
  margin-bottom: 16px;
}

.settings-form label {
  font-size: 13px;
  font-weight: 500;
  color: #344054;
  margin-bottom: 6px;
  display: block;
}

.header-mode-selector {
  display: flex;
  gap: 6px;
  margin-bottom: 8px;
}

.header-mode-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 12px;
  border: 2px solid #e9ecef;
  background: #fff;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.15s;
}

.header-mode-btn:hover {
  border-color: #d0d5dd;
  color: #495057;
}

.header-mode-btn.active {
  border-color: #556ee6;
  background: #f0f4ff;
  color: #556ee6;
}

.header-mode-btn i {
  font-size: 16px;
}

.banner-recommendation {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f0f4ff;
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 10px;
  font-size: 12px;
  color: #495057;
  line-height: 1.4;
}

.banner-recommendation i {
  color: #556ee6;
  font-size: 15px;
  flex-shrink: 0;
}

.banner-recommendation strong {
  color: #556ee6;
}

/* Upload dropzone */
.upload-dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 24px 16px;
  border: 2px dashed #d0d5dd;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
  text-align: center;
}

.upload-dropzone:hover {
  border-color: #556ee6;
  background: #f8f9ff;
}

.upload-dropzone i {
  font-size: 28px;
  color: #adb5bd;
  transition: color 0.15s;
}

.upload-dropzone:hover i {
  color: #556ee6;
}

.upload-dropzone span {
  font-size: 13px;
  font-weight: 500;
  color: #495057;
}

.upload-dropzone small {
  font-size: 11px;
  color: #adb5bd;
}

/* Upload preview */
.upload-preview {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
}

.upload-preview img {
  width: 100%;
  max-height: 80px;
  object-fit: cover;
  display: block;
}

.upload-preview-actions {
  display: flex;
  border-top: 1px solid #e9ecef;
}

.upload-preview-actions button {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 7px 10px;
  border: none;
  background: #fff;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.12s;
}

.btn-upload-change {
  color: #556ee6;
  border-right: 1px solid #e9ecef !important;
}

.btn-upload-change:hover {
  background: #f0f4ff;
}

.btn-upload-remove {
  color: #dc3545;
}

.btn-upload-remove:hover {
  background: #fef2f2;
}

/* Preview results */
.preview-results {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #eee;
  border-radius: 6px;
  margin-top: 8px;
}

.preview-result-item {
  padding: 8px 12px;
  cursor: pointer;
  transition: background 0.12s;
  border-bottom: 1px solid #f5f5f5;
}

.preview-result-item:last-child { border-bottom: none; }

.preview-result-item:hover,
.preview-result-item.selected {
  background: #e3f2fd;
}

.preview-result-item strong {
  display: block;
  font-size: 13px;
}

.preview-result-item small {
  font-size: 11px;
  color: #6c757d;
}

/* Loading Overlay */
.builder-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(245, 246, 250, 0.82);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
}

.builder-loading-content {
  text-align: center;
  background: #fff;
  padding: 32px 48px;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
}

.builder-loading-content p {
  margin: 16px 0 0;
  font-size: 15px;
  font-weight: 500;
  color: #495057;
}

.builder-loading-spinner {
  width: 36px;
  height: 36px;
  margin: 0 auto;
  border: 3px solid #e9ecef;
  border-top-color: #556ee6;
  border-radius: 50%;
  animation: builder-spin 0.8s linear infinite;
}

@keyframes builder-spin {
  to { transform: rotate(360deg); }
}

.fade-overlay-enter-active {
  transition: opacity 0.15s ease;
}

.fade-overlay-leave-active {
  transition: opacity 0.3s ease;
}

.fade-overlay-enter,
.fade-overlay-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 1400px) {
  .panel-left {
    width: 280px;
  }
  .panel-right.has-selection {
    width: 320px;
  }
}

@media (max-width: 1200px) {
  .panel-right.has-selection {
    width: 300px;
  }
}

/* Email editor */
.email-editor-intro {
  display: flex;
  gap: 10px;
  padding: 10px 12px;
  background: #f0f4ff;
  border: 1px solid #dbe4ff;
  border-radius: 6px;
  font-size: 13px;
  color: #344054;
  line-height: 1.4;
  margin-bottom: 16px;
}

.email-editor-intro i {
  font-size: 20px;
  color: #556ee6;
  flex-shrink: 0;
}

.email-tag-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
}

.email-tag-label {
  font-size: 11px;
  color: #6c757d;
  margin-right: 6px;
  font-weight: 500;
}

.email-tag-btn {
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid #bbdefb;
  background: #e3f2fd;
  color: #1565c0;
  transition: all 0.12s;
}

.email-tag-btn:hover {
  background: #bbdefb;
}

.email-editor-ck {
  border: 1px solid #e9ecef;
  border-radius: 6px;
  overflow: hidden;
}

.email-editor-ck >>> .ck-editor__editable {
  min-height: 150px;
  max-height: 300px;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 13px;
  line-height: 1.6;
  padding: 10px 12px;
}

.email-editor-ck >>> .ck.ck-toolbar {
  border-top: none;
  border-left: none;
  border-right: none;
  background: #f8f9fa;
}

.email-editor-ck >>> .ck.ck-editor__main > .ck-editor__editable {
  border: none;
}

.email-preview {
  border: 1px solid #e9ecef;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
}

.email-preview-subject {
  padding: 10px 14px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  font-size: 13px;
  font-weight: 500;
  color: #344054;
}

.email-preview-label {
  font-size: 11px;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-right: 6px;
}

.email-preview-body {
  padding: 14px;
  font-size: 13px;
  line-height: 1.6;
  color: #333;
  max-height: 220px;
  overflow-y: auto;
}

.email-preview-body p {
  margin: 0 0 10px 0;
}

.email-preview-note {
  padding: 8px 14px;
  background: #f0fdf4;
  border-top: 1px dashed #86efac;
  font-size: 12px;
  color: #166534;
}

.email-preview-note i {
  margin-right: 4px;
}
</style>

<style>
/* Global style to make builder fill viewport (same as card-builder) */
body.form-builder-page {
  overflow: hidden !important;
  height: 100vh !important;
}

body.form-builder-page footer.footer {
  display: none !important;
}

body.form-builder-page .main-content {
  padding: 0 !important;
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  margin-right: 0 !important;
  overflow: hidden !important;
  height: 100vh !important;
  display: flex !important;
  flex-direction: column !important;
}

body.form-builder-page .page-content {
  padding: 0 !important;
  margin: 0 !important;
  margin-top: 70px !important;
  overflow: hidden !important;
  min-height: 0 !important;
  height: calc(100vh - 70px) !important;
  max-height: calc(100vh - 70px) !important;
  flex: 1 !important;
  display: flex !important;
  flex-direction: column !important;
}

body.form-builder-page .page-content > .container-fluid {
  padding: 0 !important;
  margin: 0 !important;
  height: 100% !important;
  min-height: 0 !important;
  max-width: 100% !important;
  flex: 1 !important;
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;
}
</style>
